import IconButton from '@mui/material/IconButton';
import { Autocomplete, Paper, PaperProps, TextField } from '@mui/material';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import {
  AutocompleteResType,
  AutocompleteResTypeData,
} from '../../services/api';

const styles = {
  background: '#FFFFFF',
  border: 'unset',
  padding: '0',
  zIndex: 1001,
  borderBottom: '1px solid',
  borderColor: '#EDEDED',
  '& + .MuiAutocomplete-popper .MuiAutocomplete-option:hover': {
    backgroundColor: '#EEF7FD',
  },
  '& + .MuiAutocomplete-popper .MuiAutocomplete-option.Mui-focused': {
    backgroundColor: '#EEF7FD',
  },
  '& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected="true"]': {
    backgroundColor: 'white',
  },
  '& .MuiOutlinedInput-root': {
    paddingRight: '12px !important',
    paddingLeft: '0',
    fontSize: '1rem',
    '& fieldset': {
      border: 'unset',
    },
    '& input': {
      paddingTop: '0',
      paddingBottom: '0',
    },
  },
};

const CustomPaper = (props: PaperProps) => {
  return <Paper sx={{ margin: '8px', maxHeight: '250px' }} {...props} />;
};

interface AutocompleteComponentProps {
  placeholder?: string;
  search: (value: string) => void;
  submitSearch: (value?: AutocompleteResTypeData) => void;
  inputValue: string | null;
  selectedValue: AutocompleteResTypeData | undefined;
  setValue: (value: AutocompleteResTypeData) => void;
  setInputValue: (value: string | null) => void;
  autoCompleteResults: undefined | AutocompleteResType;
  isPending: boolean;
}

export const AutocompleteComponent = ({
  placeholder,
  search,
  submitSearch,
  inputValue,
  selectedValue,
  setValue,
  setInputValue,
  autoCompleteResults,
  isPending,
}: AutocompleteComponentProps) => {
  return (
    <Autocomplete
      data-testid="search-bar"
      freeSolo={selectedValue && autoCompleteResults?.data ? false : true}
      isOptionEqualToValue={(option, value) => {
        return (
          option.title === value.title && option.searchType === value.searchType
        );
      }}
      filterOptions={(options, value) => {
        const newOptions: AutocompleteResTypeData[] = [];
        options.forEach((option) => {
          // By default mui looks for exact matches between input value and autocomplete results
          // This is removing puncuation and white space when comparing input value to autocomplete results to make matching less restrictive
          if (
            typeof option !== 'string' &&
            option?.title &&
            option.title
              .replace(/[^\w]|_/g, '')
              .toLowerCase()
              .includes(
                value?.inputValue?.replace(/[^\w]|_/g, '').toLowerCase()
              )
          ) {
            newOptions.push(option);
          }
        });
        return newOptions;
      }}
      disablePortal
      noOptionsText={'No results found...'}
      autoHighlight
      blurOnSelect
      loading={isPending}
      loadingText={'Searching...'}
      sx={styles}
      fullWidth
      inputValue={inputValue ?? ''}
      onInputChange={(_event, value) => {
        setInputValue(value);
      }}
      value={selectedValue || null}
      onChange={(_event, value) => {
        setValue(value as AutocompleteResTypeData);
        submitSearch(value as AutocompleteResTypeData);
      }}
      options={
        // Remove duplicates
        autoCompleteResults?.data.filter(
          (v, i, a) =>
            a.findIndex(
              (t) => t.title === v.title && t.searchType === v.searchType
            ) === i
        ) || []
      }
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option?.title || ''
      }
      getOptionKey={(option) =>
        option && typeof option !== 'string'
          ? `${option.title}-${option.searchType}`
          : ''
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder || 'Search'}
          variant="outlined"
          onChange={(e) => search(e.target.value)}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <IconButton
                  disabled={!selectedValue}
                  onClick={() => submitSearch()}
                  sx={{ padding: '10px', paddingLeft: '0' }}
                  aria-label="search"
                >
                  <SearchOutlined />
                </IconButton>
              ),
            },
          }}
        />
      )}
      slots={{
        paper: CustomPaper,
      }}
    />
  );
};


